import React, { useEffect, useState } from "react";
import Layout from "../../../components/Layout";
import Container from "../../../components/Container";
import DefaultBreadcrumbs from "../../../components/DefaultBreadcrumbs";
import Flex from "../../../components/Flex";
import { useAuthState } from "../../../components/AuthProvider";
import { useMessageToast } from "../../../components/MessageProvider";
import Content from "../../../components/Content";
import DataTable from "../../../components/DataTable";
import ButtonList from "../../../components/ButtonList";
import LinkButton from "../../../components/LinkButton";
import { useHistory } from "react-router-dom";
import handleResponse from "../../../utils/handleFetchResponse";
import fetchConfig from "../../../utils/fetchConfig";

import styles from "./ExternalVendors.module.scss";
import Spinner from "../../../components/Loader/Spinner";

const ExternalVendors = () => {
  const externalVendorApiUrl = `${process.env.REACT_APP_SVC_APIS_DOMAIN}/externalvendor/get`;
  const history = useHistory();
  const { user } = useAuthState();
  const { selectedClient } = user.preferences;
  const toast = useMessageToast();

  const [externalVendors, setExternalVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const clickableCell = ({
    cell: {
      value,
      row: { original: selectedExternalVendor }
    }
  }) => {
    const onViewClick = () => {
      history.push(`/admin/externalvendors/${selectedExternalVendor.vendorId}`);
    };

    return (
      <span className={styles.cell} onClick={onViewClick}>
        {selectedExternalVendor.vendorId}
      </span>
    );
  };

  const actionCell = ({
    cell: {
      value,
      row: { original: selectedExternalVendor }
    }
  }) => {
    const onDetailsClick = () => {
      history.push(`/admin/externalvendors/${selectedExternalVendor.vendorId}`);
    };

    const onEditClick = () => {
      history.push(
        `/admin/externalvendors/${selectedExternalVendor.vendorId}/edit`
      );
    };

    return (
      <ButtonList className={styles.icons}>
        <LinkButton icon="view" onClick={onDetailsClick}>
          Details
        </LinkButton>
        <LinkButton icon="edit" onClick={onEditClick}>
          Edit
        </LinkButton>
      </ButtonList>
    );
  };

  const externalVendorCsvColumns = [
    {
      Header: "Vendor ID",
      accessor: "vendorId"
    },
    {
      Header: "Name",
      accessor: "vendorName"
    },
    {
      Header: "Start Date",
      accessor: "startDate"
    },
    {
      Header: "Expiry Date",
      accessor: "expiryDate"
    },
    {
      Header: "DUR ID",
      accessor: "durId"
    },
    {
      Header: "Description",
      accessor: "vendorDesc"
    },
    {
      Header: "Last Updated Date",
      accessor: "lastUpdated"
    }
  ];

  const externalVendorColumns = [
    {
      Header: "Vendor ID",
      accessor: "vendorId",
      Cell: clickableCell,
      sortType: (a, b) => {
        return a.values.vendorId > b.values.vendorId ? 1 : -1;
      }
    },
    {
      Header: "Name",
      accessor: "vendorName",
      sortType: (a, b) => {
        return a.values.vendorName.toLowerCase() >
          b.values.vendorName.toLowerCase()
          ? 1
          : -1;
      }
    },
    {
      Header: "Start Date",
      accessor: "startDate",
      sortType: (a, b) => {
        return a.values.startDate > b.values.startDate ? 1 : -1;
      }
    },
    {
      Header: "Expiry Date",
      accessor: "expiryDate",
      sortType: (a, b) => {
        return a.values.expiryDate > b.values.expiryDate ? 1 : -1;
      }
    },
    {
      Header: "DUR ID",
      accessor: "durId",
      sortType: (a, b) => {
        return a.values.durId > b.values.durId ? 1 : -1;
      }
    },
    {
      Header: "Actions",
      accessor: "actions",
      disableFilters: true,
      disableSortBy: true,
      Cell: actionCell
    },
    {
      Header: "Description",
      accessor: "vendorDesc",
      disableSortBy: true
    }
  ];

  useEffect(() => {
    const config = {
      headers: {
        clientId: selectedClient.oid,
        "Content-Type": "application/json",
        userId: user.username
      }
    };
    const getExternalVendors = async () => {
      setIsLoading(true);
      const response = await fetch(externalVendorApiUrl, config);
      if ((await response.status) == 200) {
        const data = await response.json();
        setExternalVendors(data);
        setIsLoading(false);
        setHasLoaded(true);
      } else {
        toast.error("Something went wrong while fetching the vendors");
        setIsLoading(false);
      }
    };

    if (!hasLoaded) {
      setIsLoading(true);
      getExternalVendors();
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [toast, setIsLoading, setHasLoaded, hasLoaded]);

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>
            <DefaultBreadcrumbs />
          </Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                <h1>External Vendors</h1>
                <p>Select a vendor to manage its respective properties.</p>
              </Content>
              <div className={styles.base}>
                <ButtonList spacing={2} style={{ margin: "1rem 0" }}>
                  <LinkButton
                    icon="add"
                    onClick={() => {
                      history.push("/admin/externalvendors/add");
                    }}
                    disabled={
                      user.role === "Organization Admin" &&
                      window.localStorage.getItem("LOGIN_TYPE") === "MS-Email"
                    }
                  >
                    Add Vendor
                  </LinkButton>
                </ButtonList>
                <div>
                  <DataTable
                    columns={externalVendorColumns}
                    csvColumns={externalVendorCsvColumns}
                    data={externalVendors}
                    hasPagination
                    hasFiltering
                    hasSorting
                    hasDownloadCsv={true}
                  />
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default ExternalVendors;
